import React from 'react';
import styled from 'styled-components';
import { Dialog, DialogContent } from '@mui/material';
import SimpleButton from '../molecules/Button/SimpleButton';
import Icon from '../Icon';
import Typo from '../Typo';
import { breakpoints, media } from '../../utils/media';
import { useTranslation } from 'react-i18next';
import theme from '../theme';
import { toRgba } from '../theme/colors';

const DialogWrapper = styled(Dialog)`
  &.MuiModal-root.MuiDialog-root {
    z-index: 9999;
  }
  .MuiBackdrop-root {
    background-color: ${toRgba(theme.colors.NEU_999, 0.76)};
  }
  .MuiDialog-paper {
    overflow: visible;
    display: flex;
    flex-direction: column;
    padding: 0;
    min-width: 400px;
    min-height: 300px;
    border-radius: 24px;
    margin-top: 40px;
    box-shadow: 0 12px 48px ${toRgba(theme.colors.NEU_999, 0.18)}, 0 4px 16px ${toRgba(theme.colors.NEU_999, 0.1)};

    ${media.lessThan('phablet')} {
      min-width: 0 !important;
      max-width: 100% !important;
      border-radius: 0;
      margin-top: 30px;
    }
  }
`;

const IconBadge = styled.div`
  position: absolute;
  top: -28px;
  left: 50%;
  transform: translateX(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.15)};
  color: ${theme.colors.NEU_0};
  z-index: 10;
`;

const ElevatedHeader = styled.div`
  padding: 40px 24px 16px;
  padding-right: 48px;
  text-align: left;
  flex-shrink: 0;
`;

const ContentWrapper = styled(DialogContent)`
  flex: 1;
  overflow-y: auto;
`;

const ElevatedFooter = styled.div`
  flex-shrink: 0;
  padding: 20px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;

  ${media.lessThan('phablet')} {
    flex-direction: column-reverse;
    gap: 10px;
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
`;

const SubtitleText = styled(Typo.SmallText)`
  color: ${({ theme }) => theme.colors.NEU_600};
  margin-top: 4px;
`;

const ModalElevated = ({
  open,
  onClose,
  showActions = true,
  onAccept,
  acceptLabel,
  acceptIsDisabled,
  acceptIsLoading = false,
  title,
  subtitle,
  icon: IconComponent = Icon.EditNote,
  children,
  width,
  maxWidth,
  ...props
}) => {
  const { t } = useTranslation();
  const fullScreen = window.innerWidth <= breakpoints.phablet;

  return (
    <DialogWrapper
      open={!!open}
      onClose={onClose}
      scroll='paper'
      fullScreen={fullScreen}
      maxWidth={maxWidth || 'md'}
      disableEnforceFocus
      sx={{ '& .MuiDialog-paper': { width, maxWidth } }}
      {...props}
    >
      <IconBadge>
        <IconComponent size='medium' />
      </IconBadge>

      {onClose && (
        <CloseButtonWrapper>
          <SimpleButton variant='ghost' size='small' icon='Close' aria-label={t('globals.close')} onClick={onClose} />
        </CloseButtonWrapper>
      )}

      <ElevatedHeader>
        {title && <Typo.ContentSmallHeader $textAlign='left'>{title}</Typo.ContentSmallHeader>}
        {subtitle && <SubtitleText>{subtitle}</SubtitleText>}
      </ElevatedHeader>

      <ContentWrapper>{children}</ContentWrapper>

      {showActions && (
        <ElevatedFooter>
          {onClose && (
            <SimpleButton variant='tertiary' bgColor={theme.colors.NEU_600} onClick={onClose}>
              {t('globals.cancel')}
            </SimpleButton>
          )}
          {onAccept && (
            <SimpleButton
              variant='primary'
              onClick={onAccept}
              disabled={acceptIsDisabled || acceptIsLoading}
              isLoading={acceptIsLoading}
              startIcon={!acceptIsLoading ? 'Save' : undefined}
            >
              {acceptLabel || t('globals.save')}
            </SimpleButton>
          )}
        </ElevatedFooter>
      )}
    </DialogWrapper>
  );
};

export default ModalElevated;
